import { addCartItem } from '@/core/redux/cartSlice';
import { setWishListCount } from '@/core/redux/commonSlice';
import store, { RootState } from '@/core/redux/store';
import { postData } from '@/services/api.service';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  FaHeart,
  FaMinus,
  FaPlus,
  FaShoppingBag,
  FaSearchPlus,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import ProductView from './Components/productView';
import { routePrefix, routes } from '@/utils/routes/routes';
import { TbEye, TbHeart, TbShoppingCartPlus } from 'react-icons/tb';
import { trackProductSelect } from '@/utils/analytics';

interface Product {
  category_id: any;
  id: number;
  name: string;
  image: string;
  price: number;
  stock: number;
  type: string;
  qty: number;
  service_mode: number
}

interface ProductCardProps {
  product: Product;
  quantity: number;
  onQuantityChange: (id: number, quantity: number) => void;
  onAddToCart: (id: number) => void;
  onAddToWishlist: (id: number) => void;
  onQuickView: (id: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  quantity,
  onQuantityChange,
  onAddToCart,
  onQuickView,
}) => {
  const [open, setOpen] = useState(false);
  const [productDetails, setProductDetails] = useState({});
  const [localQty, setLocalQty] = useState<number>(quantity);

  useEffect(() => {
    setLocalQty(quantity);
  }, [quantity]);

  const showProductDetails = (data) => {
    trackProductSelect({
      id: data?.id,
      name: data?.name,
      category_id: data?.category_id,
      price: Number(data?.price) || 0,
      variant: '',
    });
    setOpen(true);
    setProductDetails(data);
  };

  const handleClose = () => {
    setOpen(false);
    setProductDetails({});
  };

  const handleIncrement = () => {
    if (localQty < product.qty) {
      const updatedQty = localQty + 1;
      setLocalQty(updatedQty);
      onQuantityChange(product.id, updatedQty);
    } else {
      toast.warning('Maximum stock reached!');
    }
  };

  const handleDecrement = () => {
    if (localQty > 1) {
      const updatedQty = localQty - 1;
      setLocalQty(updatedQty);
      onQuantityChange(product.id, updatedQty);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= product.qty) {
      setLocalQty(value);
      onQuantityChange(product.id, value);
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );

  const user = store.getState().common.userDetails;
  const isLoggedIn = store.getState().common.isLoading;

  const onAddToWishlist = async (id: any) => {
    const payload = {
      user_id: id,
      action: 'add', // Action to delete from wishlist
      product_id: id,
    };
    try {
      if (isLoggedIn) {
        const response = await postData('wishlist', payload);
        if (response.status === 200 || response.status === 201) {
          // Remove item from the wishlist after successful deletion

          toast.success('Added to WishList');
          getWishlistList();
        }
      } else {
        toast.warning('Please login to add items to wishlist');
      }
    } catch (error) {
      console.log('Error removing item:', error);
    } finally {
    }
  };

  const getWishlistList = async () => {
    try {
      const payload = {
        user_id: user?.id,
        action: 'list',
      };
      const response = await postData('wishlist', payload);

      const items = response?.data?.data;
      dispatch(setWishListCount(items?.length || 0));
    } catch (error) {
      console.log(error);
    }
  };

  const addToCard = async () => {
    const AddToCartProduct = {
      user_id: user?.id,
      id: product?.id,
      name: product?.name,
      price: product?.price,
      image: `${product?.image_path}${product.image}`,
      category_id: product?.category_id,
      quantity: localQty,
      variant: '',
      service_mode: product?.service_mode,
    };

    dispatch(addCartItem(AddToCartProduct));
    toast.success('Item added to Cart');
    navigate(routes.cart);
  };

  const goToProductDetail = (id: any) => {
    trackProductSelect({
      id: product?.id,
      name: product?.name,
      category_id: product?.category_id,
      price: Number(product?.price) || 0,
      variant: '',
    });
    navigate(`${routePrefix}/product-details/${id}`);
  };

  return (
    <div className="single-product-wrapper">
      <div className="single-product-item">
        <Link
          to={`${routePrefix}/product-details/${product.id}`}
          className="view-details"
        >
          <img
            loading="lazy"
            className="product-img"
            src={`${product?.image_path}${product.image}`}
            alt={product.name}
          />
        </Link>
        <div className="product-overlay">
          <a>
            <span
              className="add-to-wishlist"
              onClick={() => onAddToWishlist(product.id)}
            >
              <TbHeart />
            </span>
          </a>
          <a>
            <span
              className="quick-view"
              onClick={() => showProductDetails(product)}
            >
              <TbEye />
            </span>
          </a>
        </div>
      </div>

      <div className="product-details">
        <Link
          to={`${routePrefix}/product-details/${product.id}`}
          className="product-name"
        >
          {product.name}
        </Link>
        <div className="product-price">
          <span className="price">
            {currencyCode}
            &nbsp;{product.price}
          </span>
        </div>
        <form
          className="d-flex  justify-content-between"
          onSubmit={(e) => {
            e.preventDefault();
            onAddToCart(product.id);
          }}
        >
          {product.type == 'service' ? (
            <button
              type="submit"
              className="button style1 add-to-cart-name"
              onClick={() => goToProductDetail(product.id)}
            >
              View
            </button>
          ) : product?.qty == 0 ? (
            <h4>Out of stock</h4>
          ) : (
            <>
              <div className="d-flex align-items-center">
                <div className="input-qty">
                  <button
                    type="button"
                    className="quantity-left-minus"
                    onClick={handleDecrement}
                  >
                    <FaMinus />
                  </button>
                  <input
                    type="number"
                    className="input-number"
                    value={localQty}
                    min={1}
                    max={product.qty}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="quantity-right-plus"
                    onClick={handleIncrement}
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
              <button
                type="submit"
                onClick={addToCard}
                className="button style1 add-to-cart"
              >
                <TbShoppingCartPlus />
              </button>
            </>
          )}
        </form>
      </div>
      <ProductView
        isOpen={open}
        onClose={() => handleClose()}
        product={productDetails}
      />
    </div>
  );
};

export default ProductCard;
